import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  IconButton,
  Collapse,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  LocalShipping as ShippingIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Cancel as CancelIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import StoreHeader from '../../components/layout/StoreHeader';

// Mock order data
const mockOrders = [
  {
    id: 'ORD-001',
    orderDate: '2024-01-15',
    total: 25.97,
    items: [
      {
        id: '1',
        name: 'Sympathy Card - Gentle Thoughts',
        price: 4.99,
        quantity: 2,
        image: '/images/store/sympathy-card-1.jpg',
      },
      {
        id: '2',
        name: 'Memorial Card - In Loving Memory',
        price: 5.99,
        quantity: 1,
        image: '/images/store/memorial-card-1.jpg',
      },
      {
        id: '3',
        name: 'Funeral Program - Elegant Design',
        price: 12.99,
        quantity: 1,
        image: '/images/store/funeral-program-1.jpg',
      },
    ],
    shippingAddress: {
      firstName: 'John',
      lastName: 'Smith',
      address: '123 Main St',
      city: 'Anytown',
      state: 'CA',
      zipCode: '12345',
      country: 'US',
    },
  },
  {
    id: 'ORD-002',
    orderDate: '2024-01-10',
    total: 15.98,
    items: [
      {
        id: '4',
        name: 'Memorial Bookmark - Cherished Memories',
        price: 2.99,
        quantity: 2,
        image: '/images/store/bookmark-1.jpg',
      },
      {
        id: '5',
        name: 'Sympathy Flowers - White Roses',
        price: 29.99,
        quantity: 1,
        image: '/images/store/flowers-1.jpg',
      },
    ],
    shippingAddress: {
      firstName: 'John',
      lastName: 'Smith',
      address: '123 Main St',
      city: 'Anytown',
      state: 'CA',
      zipCode: '12345',
      country: 'US',
    },
  },
];

const OrdersPage = () => {
  const [expandedOrder, setExpandedOrder] = useState(null);

  const handleOrderExpand = (orderId) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  // Parse date-only (YYYY-MM-DD) as calendar date to avoid timezone shifting the day
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const isoDate = String(dateString).split('T')[0];
    const match = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
      const [, y, m, d] = match;
      const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      return `${months[parseInt(m, 10) - 1]} ${parseInt(d, 10)}, ${y}`;
    }
    const date = new Date(dateString);
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  };

  const calculateSubtotal = (items) => {
    return items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const calculateTax = (subtotal) => {
    return subtotal * 0.08; // 8% tax
  };

  const calculateShipping = () => {
    return 5.99; // Fixed shipping cost
  };

  return (
    <>
      {/* Header */}
      <StoreHeader 
        cartCount={mockOrders.length}
      />

      <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ 
          fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
          textAlign: { xs: 'center', sm: 'left' }
        }}>
          Order History
        </Typography>

        {mockOrders.length === 0 ? (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No orders yet
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Start shopping to see your orders here
              </Typography>
              <Button variant="contained" href="/">
                Browse Store
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Box>
            {mockOrders.map((order) => (
              <Card key={order.id} sx={{ mb: 3 }}>
                <CardContent>
                  {/* Order Header */}
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between', 
                    alignItems: { xs: 'flex-start', sm: 'center' }, 
                    mb: 2,
                    gap: { xs: 2, sm: 0 }
                  }}>
                    <Box>
                      <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                        Order #{order.id}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}>
                        Placed on {formatDate(order.orderDate)}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                      <Typography variant="h6" color="primary" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                        ${order.total.toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Order Summary */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      Order Summary
                    </Typography>
                    
                    {/* Mobile Card View */}
                    <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                      {order.items.map((item) => (
                        <Card key={item.id} variant="outlined" sx={{ mb: 2, p: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Box
                              sx={{ 
                                width: 50, 
                                height: 50, 
                                backgroundColor: 'grey.200',
                                borderRadius: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                              }}
                            >
                              <ImageIcon sx={{ fontSize: 24, color: 'grey.500' }} />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                                {item.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                                Qty: {item.quantity}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                              ${item.price.toFixed(2)} each
                            </Typography>
                            <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600 }}>
                              ${(item.price * item.quantity).toFixed(2)}
                            </Typography>
                          </Box>
                        </Card>
                      ))}
                    </Box>

                    {/* Desktop Table View */}
                    <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Item</TableCell>
                              <TableCell align="right">Price</TableCell>
                              <TableCell align="right">Qty</TableCell>
                              <TableCell align="right">Total</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {order.items.map((item) => (
                              <TableRow key={item.id}>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Box
                                      sx={{ 
                                        width: 40, 
                                        height: 40, 
                                        backgroundColor: 'grey.200',
                                        borderRadius: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mr: 2
                                      }}
                                    >
                                      <ImageIcon sx={{ fontSize: 20, color: 'grey.500' }} />
                                    </Box>
                                    <Typography variant="body2">{item.name}</Typography>
                                  </Box>
                                </TableCell>
                                <TableCell align="right">${item.price.toFixed(2)}</TableCell>
                                <TableCell align="right">{item.quantity}</TableCell>
                                <TableCell align="right">${(item.price * item.quantity).toFixed(2)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  </Box>

                  {/* Order Details */}
                  <Accordion
                    expanded={expandedOrder === order.id}
                    onChange={() => handleOrderExpand(order.id)}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle2">
                        Order Details
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={3}>
                        {/* Shipping Information */}
                        <Grid xs={12} md={6}>
                          <Typography variant="subtitle2" gutterBottom>
                            Shipping Address
                          </Typography>
                          <Typography variant="body2" paragraph>
                            {order.shippingAddress.firstName} {order.shippingAddress.lastName}<br />
                            {order.shippingAddress.address}<br />
                            {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zipCode}<br />
                            {order.shippingAddress.country}
                          </Typography>
                        </Grid>

                        {/* Order Totals */}
                        <Grid xs={12} md={6}>
                          <Typography variant="subtitle2" gutterBottom>
                            Order Totals
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2">Subtotal:</Typography>
                            <Typography variant="body2">${calculateSubtotal(order.items).toFixed(2)}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2">Tax:</Typography>
                            <Typography variant="body2">${calculateTax(calculateSubtotal(order.items)).toFixed(2)}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2">Shipping:</Typography>
                            <Typography variant="body2">${calculateShipping().toFixed(2)}</Typography>
                          </Box>
                          <Divider sx={{ my: 1 }} />
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="subtitle1">Total:</Typography>
                            <Typography variant="subtitle1">${order.total.toFixed(2)}</Typography>
                          </Box>
                        </Grid>

                      </Grid>

                      {/* Action Buttons */}
                      <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                        <Button
                          variant="outlined"
                          startIcon={<DownloadIcon />}
                          size="small"
                        >
                          Download Invoice
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<PrintIcon />}
                          size="small"
                        >
                          Print Order
                        </Button>

                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </Container>
    </>
  );
};

export default OrdersPage;